import * as React from "react";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { useBlocker, useNavigate } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultMk8sInput } from "../draft/utils/utils";
import { Mk8sInput } from "../../../schema/types/mk8s/input/mk8s";
import { inputToMk8s } from "../draft/utils/inputToMk8s";
import { Mk8sCreateContent } from "./content";
import { getValidator } from "../draft/validator";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { mk8sApi } from "../../../store/api/mk8sApi";

interface Props {}
export const Mk8sCreateForm: React.FC<Props> = ({}) => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultMk8sInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/mk8s/-create`) &&
      !nextLocation.pathname.includes(`/mk8s/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: Mk8sInput) {
    try {
      let body = inputToMk8s(input);
      const method = "post";
      const url = homeLink("mk8s");

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("mk8s", body.name);
      dispatch(mk8sApi.util.invalidateTags([{ type: "Mk8sCluster", id: itemLink }]));
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "Mk8s Cluster is created",
      });
      dispatch(setRedirect(`/console${itemLink}`));
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <Mk8sCreateContent ignoreBlockerRef={ignoreBlockerRef} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal inputToItemFn={inputToMk8s} kind="mk8s" namePath="name" title="Mk8s Cluster" />
      <CpDebugModal inputToItemFn={inputToMk8s} kind="mk8s" namePath="name" title="Mk8s Cluster" />
    </CpFormContext.Provider>
  );
};
